import { AxiosError, AxiosResponse } from 'axios';
import { stringify } from 'qs';
import { MergeParams, UiResponse } from '../CommonInterfaces';
import { action, observable } from 'mobx';
import { LARES_POST_FORM_PATH, View } from '../Routes';
import { API } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import { extractHyperlink, getPathForComponent, isSameProtocolAndHost } from '../util/NavigationUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import UiStore from './UiStore';
import AccountInfoStore from './AccountInfoStore';
import { waitForModalAnimation } from '../util/AnimationUtil';

export enum MERGE_ENDPOINT {
  MERGE = '/merge',
  CANCEL = '/merge/cancel',
  CONTINUE = '/merge/continue',
  ADD_PERSONAL_CODE_CONFIRM = '/merge/add-personal-code/confirm',
  ADD_PERSONAL_CODE_CANCEL = '/merge/add-personal-code/cancel',
}

class MergeStore {
  @observable accessor submitting: boolean = false;
  @observable accessor calledMergeEndpoint: string | undefined;
  @observable accessor mergeParams: MergeParams | undefined;
  @observable accessor mergeLoginModalOpen: boolean = false;
  @observable accessor mergeConfirmationModalOpen: boolean = false;

  private accountInfoStore: AccountInfoStore;
  private routeStore: RouteStore;
  private uiStore: UiStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.uiStore = rootStore.uiStore;
    this.accountInfoStore = rootStore.accountInfoStore;
  }

  @action.bound
  public async handleLogin(url: string, refreshEmails = false, refreshAccount = false): Promise<void> {
    this.calledMergeEndpoint = url;
    this.uiStore.hideNotices();
    this.submitting = true;
    try {
      const response = await API.post(`${url}${this.routeStore.formForwardParams}`, null, undefined);
      this.handleSuccess(response);
      if (refreshEmails) {
        this.accountInfoStore.loadInitialEmailAccounts();
      }
      if (refreshAccount) {
        this.accountInfoStore.loadInitialInfo();
      }
    } catch (e: Error | string | any) {
      this.handleError(e);
    }
  }

  public canMergeAccounts(): boolean {
    if (this.resolvedMergeParams?.confirmOptions) {
      const confirmOptions = this.resolvedMergeParams?.confirmOptions;
      if (typeof confirmOptions === 'string' && confirmOptions.toLowerCase() === 'true') {
        return true;
      }
      if (typeof confirmOptions === 'boolean' && confirmOptions) {
        return true;
      }
    }
    return false;
  }

  public get resolvedMergeParams(): MergeParams {
    if (this.mergeParams && Object.keys(this.mergeParams).length) {
      return this.mergeParams;
    }
    return this.routeStore.decodedParams;
  }

  @action.bound
  public setMergeLoginModalState(isOpen: boolean) {
    this.mergeLoginModalOpen = isOpen;
  }

  public showMergeConfirmationOnAccountPage = () => {
    this.setMergeLoginModalState(false);
    waitForModalAnimation(() => this.setMergeConfirmationModalState(true));
  };

  @action.bound
  public setMergeConfirmationModalState(isOpen: boolean) {
    this.mergeConfirmationModalOpen = isOpen;
  }

  @action.bound
  private handleSuccess(response: AxiosResponse<UiResponse>) {
    const uiResponse: UiResponse = response.data;
    if (!uiResponse) {
      return;
    }
    if (uiResponse.redirectUrl) {
      if (isSameProtocolAndHost(extractHyperlink(uiResponse.redirectUrl)?.href)) {
        this.submitting = false;
      }
      return this.routeStore.route(uiResponse.redirectUrl);
    }
    if (uiResponse.component === View.LARES) {
      this.routeStore.route(LARES_POST_FORM_PATH + stringify(uiResponse.params, { addQueryPrefix: true }));
    } else if (uiResponse.component) {
      this.routeStore.route(getPathForComponent(uiResponse) + this.routeStore.formForwardParams);
    }
    this.setMergeConfirmationModalState(false);
    this.submitting = false;
    this.uiStore.handleNotice(response, false);
  }

  @action.bound
  private handleError(e: AxiosError<UiResponse>) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    this.submitting = false;
    this.mergeLoginModalOpen = false;
    this.mergeConfirmationModalOpen = false;
    if (e.response?.data.redirectUrl) {
      return this.routeStore.route(e.response.data.redirectUrl);
    }
    this.uiStore.showErrorNoticeFromResponse(e.response);
  }
}

export default MergeStore;
